import { outputTerminalCurrent } from "./output-current-limits";
import { effectiveChargeCurrent } from "./charge-current";
import { computeLiveCableCurrents } from "./live-cable-flow";
import { computePvCurrentAnalysis } from "./pv-currents";
import { computeIsolatorModelBoundary } from "./dc-isolators";
import { batteryProtectUses } from "./battery-protect";
import { isSourceFreeLoadIsland } from "./current-islands";
/** Shared current propagation. Kept independent of translated diagnostics so
 * static pages and the editor use the same calculation without browser services. */
import { dcOutputPorts } from "./dc-output-ports";
import { isAcCable, isAcTerminalId } from "./ac";
import { computeBatteryBankCurrents } from "./battery-bank";
import { componentLoadCurrents, dcDcInputCurrent, hasMissingConsumerCurrent } from "./component-current";
import { dcPortTerminalGroups, dcSideTerminals, isInverterCharger, isPvTerminalId } from "./component-ports";
import { buildDcCircuitTopology } from "./dc-circuit-topology";
import { resolveDesignCurrentGroup, type DesignDeviceMode, type UnresolvedCurrentComponent } from "./dc-design-currents";
import { currentEnvelope, sumCurrentCase, type CurrentChoiceGroup, type SignedDutyCurrent } from "./signed-current-ledger";
import { indexNodesById } from "./node-tree";
import { batterySupplyGraph } from "./battery-stack";
import type { SimulateContext } from "./cable-current";
import type { DefLookup } from "./catalog";
import { measure } from "./perf-marks";
import { applyStateToAmp, getEffectiveStateOption } from "./simulate/states";
import { computeActiveSourceCircuits, isDcSideSupplied } from "./diagnostics/graph";
import { terminalPolarity } from "./terminal-snap";
import type { ComponentDefinition, ComponentNode, System, Terminal } from "./types";
import { resolveDesignProfile } from "./design-profiles";
import {
  buildGraph, findTerminals, isBatteryNegative,
  multiSourceBfs, splitV, v, type Graph, type Vertex,
} from "./diagnostics/graph";

/**
 * Per-rail amp magnitudes under the active simulate state. Edit mode passes the
 * static spec through as worst case; a `role: "output"` state suppresses the
 * draw/surge rails, so a running engine stops reporting its starter draw.
 */
function componentAmps(
  def: ComponentDefinition,
  node: ComponentNode,
  simulate: SimulateContext | undefined,
  loadAmps: { draw: number | undefined; surge: number | undefined },
): { draw: number; surge: number; output: number } {
  const opt = simulate ? getEffectiveStateOption(def, node.id, simulate) : undefined;
  const draw = applyStateToAmp(loadAmps.draw, opt, "draw") ?? 0;
  const surge = applyStateToAmp(loadAmps.surge, opt, "draw") ?? 0;
  const output = effectiveChargeCurrent(node, def, simulate) ?? 0;
  return { draw, surge, output };
}

type RailAmps = { continuous: number; peak: number };
const NO_AMPS: RailAmps = { continuous: 0, peak: 0 };

/**
 * What one terminal pushes toward the batteries, split by direction: current a
 * part *draws* (discharge — loads, a charger's input, an engine's starter) and
 * current it *delivers* (charge — a charger's output, an alternator). The two
 * never flow through a battery trunk at the same time in the same direction —
 * an inverter running while the alternator charges means *less* on the trunk,
 * not more — so `computeCableDemands` takes the max of the two, never the sum.
 * Summing them read a 1500 W inverter's shunt lead at 242 A (152 A of loads +
 * 90 A of chargers) and a Lynx-fed catamaran trunk at 361 A.
 *
 * Engine: `starter` takes the cranking draw and its inrush, `alt-pos` only the
 * alternator's continuous output. Credit the whole node to both and a 100 A
 * alternator lead sizes as a 250 A cranking circuit.
 */
function terminalAmps(
  def: ComponentDefinition,
  term: Terminal,
  whole: { draw: number; surge: number; output: number; converterInput?: number },
): { discharge: RailAmps; charge: RailAmps } {
  const { draw, surge, output } = whole;
  const drawAmps: RailAmps = { continuous: draw, peak: Math.max(draw, surge) };
  // Bound the source contribution by its declared electronic port ceiling,
  // never by a connector rating or the loads connected downstream.
  const portOutput = outputTerminalCurrent(def, term.id, output);
  const outAmps: RailAmps = { continuous: portOutput, peak: portOutput };
  if (def.category === "engine") {
    if (term.direction === "output") return { discharge: NO_AMPS, charge: outAmps };
    // With a dedicated Alt B+ the starter stud is cranking-only; a combined
    // stud carries both (mirrors `resolveEndpoint` in cable-current.ts).
    const hasAltOutput = def.terminals.some((t) => t.direction === "output");
    return { discharge: drawAmps, charge: hasAltOutput ? NO_AMPS : outAmps };
  }
  switch (term.direction) {
    case "output":
      return { discharge: NO_AMPS, charge: outAmps };
    case "input": {
      const hasDcOutput =
        def.category === "charger" && def.terminals.some((t) => t.direction === "output");
      const input = isPvTerminalId(term.id) ? output : whole.converterInput ?? output;
      const d = draw > 0 ? draw : hasDcOutput ? input : 0;
      return { discharge: { continuous: d, peak: Math.max(d, surge) }, charge: NO_AMPS };
    }
    default:
      return { discharge: drawAmps, charge: outAmps };
  }
}

export type CableDemand = {
  peak: number;
  continuous: number;
  /** Duty-aware design ampacity where brief starter current shares a cable
   * with ordinary loads or charging. Does not grant an OCPD exemption. */
  requiredAmpacity?: number;
  /** Brief starting draw may share a protected bank conductor. */
  startingCurrent?: number;
  /** Signed-ledger sustained-current envelope, independent of brief starting
   * draw. Omitted when an unresolved legacy fallback can change the bound. */
  sustainedCurrent?: number;
  currentBasis?: "complete-route-envelope" | "provisional-fallback";
  /** Conservative bound depends on how current is allocated among complete
   * source routes; it is not an established branch operating current. */
  sourceAllocationDependent?: boolean;
};
export type CableDemandAnalysis = {
  demands: Map<string, CableDemand>;
  unresolvedComponents: UnresolvedCurrentComponent[];
  /** Every affected rail conductor, including ones omitted by legacy current
   * estimates. A provisional estimate is not a verified upper bound. */
  fallbackCableIds: ReadonlySet<string>;
  /** Uncertainty that can also invalidate a live reading. A design-only
   * converter sizing limitation does not erase a solved operating current. */
  operatingFallbackCableIds: ReadonlySet<string>;
  sourceAllocationDependentCableIds: ReadonlySet<string>;
  sourceAllocationDependentNodeIds: ReadonlySet<string>;
};

const cableDemandsCache = new WeakMap<
  System,
  WeakMap<DefLookup, Map<SimulateContext | undefined, CableDemandAnalysis>>
>();

/** A shared DC port may consume OR supply power, never subtract two unrelated
 * nameplate maxima as if both roles were simultaneous. Engines and supported
 * inverter/chargers already declare this capability. Other definitions need
 * explicit, exclusive role options; category fallback toggles are not evidence.
 * Keep this design contract to unit-gain roles and zero/off. Scaled currents,
 * nonzero overrides (which precede role filtering), and changing contact maps
 * need a per-state model before they can establish a complete design bound. */
function hasExclusiveCurrentRoles(def: ComponentDefinition): boolean {
  if (def.category === "engine" || isInverterCharger(def)) return true;
  // Dedicated converter input/output ports operate simultaneously. Only a
  // shared bidi DC positive port can use the new explicit-role capability.
  const positives = def.terminals.filter(t => t.polarity === "positive" &&
    !isAcTerminalId(t.id) && !isPvTerminalId(t.id));
  if (!positives.length || positives.some(t => t.direction !== "bidi")) return false;
  const options = def.simulateStates?.options;
  if (!options?.length || options.some(option => option.intraLinks != null)) return false;
  const active = options.filter(option => !(option.ampOverride === 0 ||
    (option.ampOverride == null && option.ampMultiplier === 0)));
  return active.some(option => option.role === "draw") &&
    active.some(option => option.role === "output") &&
    active.every(option => (option.role === "draw" || option.role === "output") &&
      option.ampOverride == null && (option.ampMultiplier == null || option.ampMultiplier === 1));
}

/** Canonical device modes shared by conductor sizing and battery demand checks. */
export function deviceDesignModes(
  nodeId: string,
  def: ComponentDefinition,
  whole: Parameters<typeof terminalAmps>[2],
  wired: ReadonlySet<string> | undefined,
): { modes: DesignDeviceMode[]; unresolved: UnresolvedCurrentComponent[] } {
  const modes = new Map<string, DesignDeviceMode>();
  const unresolved: UnresolvedCurrentComponent[] = [];
  const connectedDcPower = def.terminals.some(t => wired?.has(t.id) &&
    (t.polarity === "positive" || t.polarity === "negative" || t.polarity === "ground") &&
    !isAcTerminalId(t.id) && !isPvTerminalId(t.id));
  if (connectedDcPower) {
    if (hasMissingConsumerCurrent(def, wired))
      unresolved.push({ nodeId, reason: "missing-load-current" });
    // Engines and active DC sources need a current declaration. Missing all
    // electrical data does not declare zero draw or generation, nor certify a
    // shared conductor's known-load subtotal. Passive/sense devices stay separate.
    if (["engine", "source", "charger"].includes(def.category) && def.ampDraw == null &&
        def.ampSurge == null && def.ampOutput == null)
      unresolved.push({ nodeId, reason: "ambiguous-current-role" });
    if (!["load", "monitor", "source", "charger", "engine"].includes(def.category))
      unresolved.push({ nodeId, reason: "unsupported-component-category" });
    // Output-only legacy fields on a consuming device do not establish a
    // charging source. Retain old estimates provisionally until its draw or
    // a supported source/converter role has been specified.
    if ((def.category === "load" || def.category === "monitor") &&
        (def.ampDraw ?? 0) <= 0 && whole.draw <= 0 && whole.output > 0)
      unresolved.push({ nodeId, reason: "ambiguous-current-role" });
  }
  const exclusiveRoles = hasExclusiveCurrentRoles(def);
  const currents = new Map<string, { inputs: number; outputs: number }>();
  for (const terminal of def.terminals) {
    if (terminal.polarity !== "positive" || isAcTerminalId(terminal.id) || isPvTerminalId(terminal.id) || !wired?.has(terminal.id)) continue;
    const side = terminal.direction ?? "input";
    const portTerms = dcPortTerminalGroups(def, side).find(port => port.includes(terminal)) ?? [terminal];
    const port = {
      id: `${nodeId}:${side}:${terminal.id}`, nodeId,
      positive: [v(nodeId, terminal.id)],
      negative: portTerms.filter(t => t.polarity === "negative" || t.polarity === "ground").map(t => v(nodeId, t.id)),
    };
    const per = terminalAmps(def, terminal, whole);
    if (isInverterCharger(def)) {
      const output = outputTerminalCurrent(def, terminal.id, whole.output);
      per.charge = { continuous: output, peak: output };
    }
    if (!exclusiveRoles && per.discharge.peak > 0 && per.charge.peak > 0)
      unresolved.push({ nodeId, portId: port.id, reason: "ambiguous-current-role" });
    for (const [role, amps] of [["draw", per.discharge], ["output", per.charge]] as const) {
      if (amps.peak <= 0) continue;
      const modeId = exclusiveRoles ? role : "on";
      const mode = modes.get(modeId) ?? { id: `${nodeId}:${modeId}`, ports: [] };
      const sign = role === "draw" ? 1 : -1;
      const brief = def.category === "engine" && role === "draw";
      mode.ports.push({ port, current: {
        sustainedA: brief ? 0 : sign * amps.continuous,
        startingA: brief ? amps.continuous : 0,
        peakA: sign * amps.peak,
      } });
      modes.set(modeId, mode);
      const total = currents.get(modeId) ?? { inputs: 0, outputs: 0 };
      if (role === "draw") total.inputs++;
      else total.outputs += amps.continuous;
      currents.set(modeId, total);
    }
  }
  // Several physical ports do not each inherit an aggregate stage rating.
  // Retain legacy bounds until allocation of that shared rating is explicit.
  if ([...currents.values()].some(total => total.inputs > 1 || total.outputs > whole.output + 1e-9))
    unresolved.push({ nodeId, reason: "shared-port-rating" });
  return { modes: [...modes.values()], unresolved };
}

function unknownDirectionGroup(nodeId: string, def: ComponentDefinition, discharge: ReadonlyMap<string, CableDemand>, charge: ReadonlyMap<string, CableDemand>): CurrentChoiceGroup {
  type Alternative = CurrentChoiceGroup["alternatives"][number];
  const off: Alternative = { id: `${nodeId}:off`, byCable: new Map() };
  const roleChoices = ([['draw', discharge], ['charge', charge]] as const).map(([role, demands]) =>
    [-1, 1].map(sign => {
      const brief = role === "draw" && def.category === "engine";
      return { id: `${nodeId}:${role}:${sign}`, byCable: new Map([...demands].map(([id, demand]) => [id, {
        sustainedA: brief ? 0 : sign * demand.continuous,
        startingA: brief ? sign * demand.continuous : 0,
        peakA: sign * demand.peak,
      } satisfies SignedDutyCurrent])) };
    }));
  // An engine starts or charges; an inverter-charger inverts or charges.
  // Ordinary converters consume input while delivering output. Even when
  // route direction is unknown, their known ledger contributions can add on
  // a shared midpoint conductor and must not become exclusive alternatives.
  const modes = hasExclusiveCurrentRoles(def)
    ? roleChoices.flat()
    : roleChoices[0].flatMap(draw => roleChoices[1].map(charge => ({
        id: `${draw.id}|${charge.id}`, byCable: sumCurrentCase([draw, charge]),
      })));
  return { id: nodeId, alternatives: [off, ...modes] };
}

/**
 * A battery-ward flow DAG for one rail. `dist` is hops-to-nearest-battery, `out`
 * each vertex's edges toward smaller `dist` weighted by the batteries reachable
 * past it, and `order` descending `dist`, so a vertex is reached only after all
 * its inflow. `mask[v]` keeps those batteries as one bit per seed so a walk can
 * re-weight an edge against a subset of them (see {@link creditDemandSplit}).
 */
type FlowEdge = { to: Vertex; cableId: string | null; weight: number };
type FlowRail = {
  dist: Map<Vertex, number>;
  out: Map<Vertex, FlowEdge[]>;
  order: Vertex[];
  mask: Map<Vertex, bigint>;
  /** Seed vertex → its bit in `mask`. */
  seedBit: Map<Vertex, bigint>;
};

/** Weight of the seeds in `mask`, each seed's weight given per bit. */
function maskWeight(mask: bigint, weights: ReadonlyArray<[bigint, number]>): number {
  let w = 0;
  for (const [bit, weight] of weights) if ((mask & bit) !== 0n) w += weight;
  return w;
}

/**
 * `seedWeight` is how parallel batteries share what reaches them together:
 * capacity-weighted like `battery-bank.ts`, or equal when a battery in the
 * system carries no `capacityAh` (one unknown makes every ratio unknown).
 */
function buildFlowRail(
  graph: Graph,
  seeds: Vertex[],
  seedWeight: (seed: Vertex) => number,
): FlowRail {
  const { dist } = multiSourceBfs(graph, seeds);
  // One bit per battery seed; `mask[v]` is the batteries reachable battery-ward
  // from v. Increasing `dist` is what resolves a vertex's neighbours before it.
  const seedBit = new Map<Vertex, bigint>();
  const weights: Array<[bigint, number]> = [];
  seeds.forEach((s, i) => {
    const bit = 1n << BigInt(i);
    seedBit.set(s, bit);
    weights.push([bit, seedWeight(s)]);
  });
  const asc = [...dist.keys()].sort((a, b) => dist.get(a)! - dist.get(b)!);
  const mask = new Map<Vertex, bigint>();
  for (const vtx of asc) {
    let m = seedBit.get(vtx) ?? 0n;
    const dv = dist.get(vtx)!;
    for (const { vertex: u } of graph.adj.get(vtx) ?? []) {
      const du = dist.get(u);
      if (du != null && du < dv) m |= mask.get(u) ?? 0n;
    }
    mask.set(vtx, m);
  }
  const out = new Map<Vertex, FlowEdge[]>();
  for (const vtx of asc) {
    const dv = dist.get(vtx)!;
    const edges: FlowEdge[] = [];
    for (const { vertex: u, via } of graph.adj.get(vtx) ?? []) {
      const du = dist.get(u);
      if (du == null || du >= dv) continue; // not battery-ward
      edges.push({
        to: u,
        cableId: via.kind === "cable" ? via.cableId : null,
        weight: maskWeight(mask.get(u) ?? 0n, weights),
      });
    }
    out.set(vtx, edges);
  }
  return { dist, out, order: [...asc].reverse(), mask, seedBit };
}

/** What landed on each battery seed: continuous amps, or peak where the
 *  terminal only surges. The proportions are what the other rail mirrors. */
type SinkShares = Map<Vertex, number>;

/**
 * Push one terminal's demand back toward the batteries, splitting at parallel
 * branches by edge weight. `seen` dedups per component, so a multi-port input
 * (an MPPT's PV + battery positives) counts its shared trunk once, not twice.
 *
 * `restrict` re-weights every split to a subset of the seeds — the batteries
 * the same component's *other* rail actually reached, in the proportions it
 * reached them — so a return goes back to the battery whose positive post
 * sourced or sank it (KCL) instead of to whichever post is fewest hops away.
 * A split none of those batteries lie past falls back to the rail's own
 * weights rather than stranding the current.
 *
 * Returns the amps that reached each seed, for the other rail to mirror.
 */
function creditDemandSplit(
  rail: FlowRail,
  start: Vertex,
  peak: number,
  continuous: number,
  map: Map<string, CableDemand>,
  seen: Set<string>,
  restrict?: ReadonlyArray<[bigint, number]>,
): SinkShares {
  const inflowPeak = new Map<Vertex, number>([[start, peak]]);
  const inflowCont = new Map<Vertex, number>([[start, continuous]]);
  const sinks: SinkShares = new Map();
  for (const vtx of rail.order) {
    const fp = inflowPeak.get(vtx) ?? 0;
    const fc = inflowCont.get(vtx) ?? 0;
    if (fp <= 0 && fc <= 0) continue;
    const edges = rail.out.get(vtx);
    if (!edges || edges.length === 0) {
      // Reached a battery (sink).
      if (rail.seedBit.has(vtx)) sinks.set(vtx, (sinks.get(vtx) ?? 0) + (fc > 0 ? fc : fp));
      continue;
    }
    let weights: number[] | null = null;
    let totW = 0;
    if (restrict) {
      weights = edges.map((e) => maskWeight(rail.mask.get(e.to) ?? 0n, restrict));
      for (const w of weights) totW += w;
    }
    if (totW <= 0) {
      weights = null;
      totW = 0;
      for (const e of edges) totW += e.weight;
    }
    if (totW <= 0) continue;
    for (let i = 0; i < edges.length; i++) {
      const e = edges[i];
      const frac = (weights ? weights[i] : e.weight) / totW;
      if (frac <= 0) continue;
      const ep = fp * frac;
      const ec = fc * frac;
      if (e.cableId != null && !seen.has(e.cableId)) {
        seen.add(e.cableId);
        const cur = map.get(e.cableId) ?? { peak: 0, continuous: 0 };
        map.set(e.cableId, { peak: cur.peak + ep, continuous: cur.continuous + ec });
      }
      inflowPeak.set(e.to, (inflowPeak.get(e.to) ?? 0) + ep);
      inflowCont.set(e.to, (inflowCont.get(e.to) ?? 0) + ec);
    }
  }
  return sinks;
}

/**
 * The seed weights the other rail should split by: each of `rail`'s seeds gets
 * the amps that landed on its battery over there. Empty when nothing landed —
 * a component whose other rail is unwired keeps the hop-nearest split.
 */
function mirrorWeights(rail: FlowRail, landed: SinkShares): Array<[bigint, number]> {
  if (landed.size === 0) return [];
  const byBattery = new Map<string, number>();
  for (const [seed, amps] of landed) {
    const { nodeId } = splitV(seed);
    byBattery.set(nodeId, (byBattery.get(nodeId) ?? 0) + amps);
  }
  const out: Array<[bigint, number]> = [];
  for (const [seed, bit] of rail.seedBit) {
    const amps = byBattery.get(splitV(seed).nodeId);
    if (amps != null && amps > 0) out.push([bit, amps]);
  }
  return out;
}

/** Static mode builds signed complete-source route envelopes: source choices
 * and device modes are exclusive, while separate devices may operate together.
 * Unsupported components retain the legacy estimate and explicit provenance.
 * Simulate mode keeps the existing dispatched-current solver. */
export function computeCableDemands(
  system: System,
  defLookup: DefLookup,
  simulate?: SimulateContext,
): Map<string, CableDemand> {
  return computeCableDemandAnalysis(system, defLookup, simulate).demands;
}

export function computeCableDemandAnalysis(
  system: System,
  defLookup: DefLookup,
  simulate?: SimulateContext,
): CableDemandAnalysis {
  let byLookup = cableDemandsCache.get(system);
  if (!byLookup) {
    byLookup = new WeakMap();
    cableDemandsCache.set(system, byLookup);
  }
  let bySim = byLookup.get(defLookup);
  if (!bySim) {
    bySim = new Map();
    byLookup.set(defLookup, bySim);
  }
  const cached = bySim.get(simulate);
  if (cached) return cached;
  const result = measure("computeCableDemands", () =>
    computeCableDemandsImpl(system, defLookup, simulate),
  );
  bySim.set(simulate, result);
  return result;
}

function computeCableDemandsImpl(
  system: System,
  defLookup: DefLookup,
  simulate?: SimulateContext,
): CableDemandAnalysis {
  const headroom = resolveDesignProfile(system).policy.continuousLoadHeadroom;
  const map = new Map<string, CableDemand>();
  const unresolvedComponents: UnresolvedCurrentComponent[] = [];
  const designGroups: CurrentChoiceGroup[] = [];
  const representedLegacyCableIds = new Set<string>();
  const fallbackCableIds = new Set<string>();
  const designOnlyFallbackCableIds = new Set<string>();
  const isolatedLoadFallbackCableIds = new Set<string>();
  const sourceAllocationDependentCableIds = new Set<string>();
  const sourceAllocationDependentNodeIds = new Set<string>();
  const topology = !simulate ? buildDcCircuitTopology(system, defLookup) : undefined;
  const nodesById = indexNodesById(system.nodes);
  // With simulate, an open switch drops out of the graph; without it, loads
  // downstream of it propagate demand as if it were closed.
  const graph = buildGraph(system, defLookup, { simulate });

  const battNeg = findTerminals(system, defLookup, isBatteryNegative);
  const supply = batterySupplyGraph(system, defLookup, graph);
  // Batteries reached together share by capacity, as `battery-bank.ts` splits a
  // bank; one battery without a figure makes the ratio unknowable, so all of
  // them share equally rather than one being read as empty.
  const capacityByBattery = new Map<string, number>();
  let allCapacitiesKnown = true;
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def?.category !== "battery") continue;
    const ah = def.capacityAh ?? 0;
    if (ah <= 0) allCapacitiesKnown = false;
    capacityByBattery.set(node.id, ah);
  }
  const seedWeight = (seed: Vertex): number =>
    allCapacitiesKnown ? (capacityByBattery.get(splitV(seed).nodeId) ?? 1) : 1;
  const flowPos =
    supply.positives.length > 0 ? buildFlowRail(supply.graph, supply.positives, seedWeight) : null;
  const flowNeg = battNeg.length > 0 ? buildFlowRail(graph, battNeg, seedWeight) : null;

  // Live loads require both rails of one actual source circuit. Other roles
  // retain their rail-specific design-demand gate; edit mode is conservative.
  const wiredTerminalIds = new Map<string, Set<string>>();
  for (const c of system.cables) {
    if (!wiredTerminalIds.has(c.fromNodeId)) wiredTerminalIds.set(c.fromNodeId, new Set());
    if (!wiredTerminalIds.has(c.toNodeId)) wiredTerminalIds.set(c.toNodeId, new Set());
    wiredTerminalIds.get(c.fromNodeId)!.add(c.fromTerminalId);
    wiredTerminalIds.get(c.toNodeId)!.add(c.toTerminalId);
  }
  // Uncertainty follows all connected rail conductors, including source
  // series links. This records influence only; it never turns a battery or
  // converter into a conductive edge or invents an operating current.
  const markConnectedRailFallback = (seeds: Iterable<Vertex>, target = fallbackCableIds) => {
    if (!topology) return;
    const pending = [...seeds], visited = new Set(pending);
    const enqueue = (vertex: Vertex) => {
      if (!visited.has(vertex)) { visited.add(vertex); pending.push(vertex); }
    };
    for (let i = 0; i < pending.length; i++) {
      for (const edge of topology.graph.adj.get(pending[i]) ?? []) {
        if (edge.via.kind === "cable") target.add(edge.via.cableId);
        enqueue(edge.vertex);
      }
      const posts = topology.batteryPorts.get(splitV(pending[i]).nodeId);
      if (posts) for (const vertex of [...posts.positive, ...posts.negative]) enqueue(vertex);
    }
  };
  const cableEnds = (ids: ReadonlySet<string>) => system.cables.filter(c => ids.has(c.id))
    .flatMap(c => [v(c.fromNodeId, c.fromTerminalId), v(c.toNodeId, c.toTerminalId)]);
  const pv = !simulate ? computePvCurrentAnalysis(nodesById, system.cables, defLookup) : undefined;
  if (pv) {
    unresolvedComponents.push(...pv.unresolvedComponents);
    for (const id of pv.unresolvedCableIds) fallbackCableIds.add(id);
    markConnectedRailFallback(cableEnds(pv.unresolvedCableIds));
    const isolators = computeIsolatorModelBoundary(system, defLookup);
    for (const nodeId of isolators.nodeIds) unresolvedComponents.push({ nodeId, reason: "one-way-device" });
    for (const id of isolators.cableIds) fallbackCableIds.add(id);
    for (const use of batteryProtectUses(system, defLookup)) {
      unresolvedComponents.push({ nodeId: use.nodeId, reason: "restricted-device-use" });
      markConnectedRailFallback([v(use.nodeId, "in"), v(use.nodeId, "out")]);
    }
  }
  const activeSources = simulate ? computeActiveSourceCircuits(system, defLookup, simulate) : [];
  const hasClosedCircuit = (def: ComponentDefinition, nodeId: string): boolean => {
    if (!simulate) return true;
    if (def.category === "load") return isDcSideSupplied({ id: nodeId }, def, "input", activeSources);
    const wired = wiredTerminalIds.get(nodeId);
    if (!wired) return true;
    for (const t of def.terminals) {
      if (!wired.has(t.id)) continue;
      const pol = terminalPolarity(t);
      if (pol === "other") continue;
      const start = v(nodeId, t.id);
      const reachable =
        pol === "positive"
          ? (flowPos?.dist.has(start) ?? false)
          : (flowNeg?.dist.has(start) ?? false);
      if (!reachable) return false;
    }
    return true;
  };

  const discharge = new Map<string, CableDemand>();
  const charge = new Map<string, CableDemand>();
  const starting = new Map<string, CableDemand>();
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (!def) continue;
    // A battery supplies current; skipping it stops a declared `maxDischargeA`
    // being credited as a load. Distribution hardware carries current, it does
    // not draw it: a bus bar's catalog `ampOutput` (its stud rating) once
    // injected a phantom 250 A load per MaxiBus.
    if (def.category === "battery" || def.category === "distribution") continue;
    const amps = componentAmps(def, node, simulate, componentLoadCurrents(def, node.id, nodesById, system.cables, defLookup, simulate));
    const whole = { ...amps, converterInput: dcDcInputCurrent(def, node.id, nodesById, system.cables, defLookup, amps.output, simulate) };
    const request = !simulate && def.category !== "solar"
      ? deviceDesignModes(node.id, def, whole, wiredTerminalIds.get(node.id)) : undefined;
    if (request?.modes.length && whole.draw <= 0 && whole.converterInput != null && def.maxInputA != null && def.maxInputA > 0) {
      const uncappedInput = dcDcInputCurrent({ ...def, maxInputA: undefined }, node.id, nodesById, system.cables, defLookup, amps.output);
      // The design mode still uses the full output rating. An input cap makes
      // that input/output pair inconsistent, so subtracting their nominal
      // currents can falsely certify zero on common-return copper. Retain the
      // conservative fallback until design modes couple both sides of the
      // limited conversion stage, as the live dispatcher already does.
      if (uncappedInput != null && uncappedInput > whole.converterInput + 1e-9)
        request.unresolved.push({ nodeId: node.id, reason: "converter-current-coupling" });
    }
    if (Math.max(whole.draw, whole.surge, whole.output) <= 0 && !request?.unresolved.length) continue;
    if (!hasClosedCircuit(def, node.id)) continue;

    // Keep starter demand separate so its design bound can include accessory
    // loads without adding opposing alternator output or 1.25× derating the
    // brief crank. Retain the ordinary walk for incomplete starter drawings.
    const componentDischarge = !simulate ? new Map<string, CableDemand>() : discharge;
    const componentCharge = !simulate ? new Map<string, CableDemand>() : charge;
    const seenDischarge = new Set<string>();
    const seenCharge = new Set<string>();
    // Positive terminals first: where a ledger's current lands on that rail is
    // where the same ledger's return is sent on the other. The pairing is per
    // ledger, not per component, so a DC-DC's IN− follows its IN+ to the start
    // battery while its OUT− follows OUT+ to the house battery.
    const landedDischarge: SinkShares = new Map();
    const landedCharge: SinkShares = new Map();
    const positives: Terminal[] = [];
    const negatives: Terminal[] = [];
    for (const t of def.terminals) {
      // A charger's AC ⏚ is `ground` polarity and `bidi`, which read as its
      // whole `ampOutput` pushed down the PE to DC negative: 30 A of "DC" on a
      // protective earth. No DC demand starts on an AC terminal.
      if (isAcTerminalId(t.id)) continue;
      const pol = terminalPolarity(t);
      if (pol === "positive") positives.push(t);
      else if (pol === "negative") negatives.push(t);
    }
    if (flowPos) {
      for (const t of positives) {
        const start = v(node.id, t.id);
        if (!flowPos.dist.has(start)) continue;
        const per = terminalAmps(def, t, whole);
        if (per.discharge.peak > 0) {
          const landed = creditDemandSplit(flowPos, start, per.discharge.peak, per.discharge.continuous, componentDischarge, seenDischarge);
          for (const [seed, amps] of landed) landedDischarge.set(seed, (landedDischarge.get(seed) ?? 0) + amps);
        }
        if (per.charge.peak > 0) {
          const landed = creditDemandSplit(flowPos, start, per.charge.peak, per.charge.continuous, componentCharge, seenCharge);
          for (const [seed, amps] of landed) landedCharge.set(seed, (landedCharge.get(seed) ?? 0) + amps);
        }
      }
    }
    if (flowNeg && (def.category !== "load" || landedDischarge.size > 0)) {
      const mirrorDischarge = mirrorWeights(flowNeg, landedDischarge);
      const mirrorCharge = mirrorWeights(flowNeg, landedCharge);
      for (const t of negatives) {
        const start = v(node.id, t.id);
        if (!flowNeg.dist.has(start)) continue;
        const per = terminalAmps(def, t, whole);
        // Some non-isolated DC/DCs (e.g. REDARC BCDC) expose one common
        // negative on the output side. It carries each ledger back to the
        // bank its positive reached; an unused PV− is not an ALT return.
        const commonDcReturn = whole.converterInput != null && t.direction === "output" &&
          dcSideTerminals(def, "input").some(input => input.id === t.id);
        if (commonDcReturn) {
          const input = whole.draw > 0 ? whole.draw : whole.converterInput!;
          per.discharge = { continuous: input, peak: Math.max(input, whole.surge) };
        }
        if (per.discharge.peak > 0) {
          creditDemandSplit(
            flowNeg, start, per.discharge.peak, per.discharge.continuous, componentDischarge, seenDischarge,
            mirrorDischarge.length > 0 ? mirrorDischarge : undefined,
          );
        }
        if (per.charge.peak > 0) {
          creditDemandSplit(
            flowNeg, start, per.charge.peak, per.charge.continuous, componentCharge, seenCharge,
            mirrorCharge.length > 0 ? mirrorCharge : undefined,
          );
        }
      }
    }
    if (componentDischarge !== discharge) {
      for (const [from, to] of [[componentDischarge, def.category === "engine" ? starting : discharge], [componentCharge, charge]]) {
        for (const [id, current] of from) {
          const previous = to.get(id);
          to.set(id, { continuous: (previous?.continuous ?? 0) + current.continuous, peak: (previous?.peak ?? 0) + current.peak });
        }
      }
      // PV current stays in its separate string model; its generic +/−
      // terminal names do not turn the panel into a battery-side DC port.
      if (def.category === "solar") continue;
      const operatingIssues = request!.unresolved.filter(item => item.reason !== "converter-current-coupling");
      const designOnlyIssues = request!.unresolved.filter(item => item.reason === "converter-current-coupling");
      // A design-only input/output limit mismatch must not skip route checks:
      // missing returns and parallel copper still invalidate live readings.
      const resolved: ReturnType<typeof resolveDesignCurrentGroup> = operatingIssues.length
        ? { unresolved: operatingIssues, sourceAllocationDependentCableIds: new Set() }
        : resolveDesignCurrentGroup(topology!, node.id, request!.modes);
      if (resolved.group) {
        if (designOnlyIssues.length) {
          unresolvedComponents.push(...designOnlyIssues);
          const conservative = unknownDirectionGroup(node.id, def, componentDischarge, componentCharge);
          // Keep complete transfer routes as a floor: the legacy walk can
          // miss a battery trunk. These are alternatives for ONE converter,
          // never independent demands that would count its rating twice.
          designGroups.push({ ...resolved.group, alternatives: [...resolved.group.alternatives, ...conservative.alternatives] });
          for (const alternative of resolved.group.alternatives)
            for (const id of alternative.byCable.keys()) designOnlyFallbackCableIds.add(id);
          for (const id of [...componentDischarge.keys(), ...componentCharge.keys()]) designOnlyFallbackCableIds.add(id);
        } else designGroups.push(resolved.group);
        for (const id of resolved.sourceAllocationDependentCableIds) sourceAllocationDependentCableIds.add(id);
        for (const id of [...componentDischarge.keys(), ...componentCharge.keys()]) representedLegacyCableIds.add(id);
      } else {
        unresolvedComponents.push(...resolved.unresolved, ...designOnlyIssues);
        designGroups.push(unknownDirectionGroup(node.id, def, componentDischarge, componentCharge));
        for (const id of [...componentDischarge.keys(), ...componentCharge.keys()]) fallbackCableIds.add(id);
        // An unsupported transfer may use copper missed by the old nearest-
        // battery walk. Uncertainty follows its whole connected rail: a small
        // resolved load on that rail cannot certify the cable's total current
        // and suppress existing local/bank bounds. These estimates remain
        // explicitly unverified; they are not a solution for parallel copper.
        const seeds = def.terminals.filter(t => wiredTerminalIds.get(node.id)?.has(t.id) &&
          (t.polarity === "positive" || t.polarity === "negative" || t.polarity === "ground") &&
          !isAcTerminalId(t.id) && !isPvTerminalId(t.id)).map(t => v(node.id, t.id));
        const isolatedLoad = resolved.unresolved.length > 0 &&
          resolved.unresolved.every(item => item.reason === "no-source") &&
          componentDischarge.size === 0 && componentCharge.size === 0 &&
          isSourceFreeLoadIsland(topology!, node.id, request!.modes, nodesById, system.cables, defLookup);
        markConnectedRailFallback(seeds, isolatedLoad ? isolatedLoadFallbackCableIds : fallbackCableIds);
      }
    }
  }
  // Loads on a batteryless DC output still impose real fuse, conductor and
  // voltage-drop demand. Never bridge the converter: route each load only to
  // its declared output pair, preserving separate voltage/current domains.
  const outputRails = dcOutputPorts(system, defLookup).map(port => ({
    positive: buildFlowRail(graph, port.positive, () => 1),
    negative: buildFlowRail(graph, port.negative, () => 1),
  }));
  for (const node of system.nodes) {
    if (node.kind !== "component") continue;
    const def = defLookup(node.definitionId, node.customDefinition);
    if (def?.category !== "load" || !hasClosedCircuit(def, node.id)) continue;
    const inputs = dcSideTerminals(def, "input");
    const positive = inputs.filter(t => t.polarity === "positive");
    if (positive.some(t => flowPos?.dist.has(v(node.id, t.id)))) continue;
    const rail = outputRails.find(rail => positive.some(t => rail.positive.dist.has(v(node.id, t.id))));
    if (!rail) continue;
    const amps = componentAmps(def, node, simulate, componentLoadCurrents(def, node.id, nodesById, system.cables, defLookup, simulate));
    const seen = new Set<string>();
    for (const t of inputs) {
      const flow = t.polarity === "positive" ? rail.positive : rail.negative;
      const start = v(node.id, t.id);
      if (flow.dist.has(start)) creditDemandSplit(flow, start, Math.max(amps.draw, amps.surge), amps.draw, discharge, seen);
    }
  }
  for (const id of new Set([...discharge.keys(), ...charge.keys(), ...starting.keys()])) {
    const d = discharge.get(id);
    const c = charge.get(id);
    const s = starting.get(id);
    map.set(id, {
      peak: Math.max((d?.peak ?? 0) + (s?.peak ?? 0), c?.peak ?? 0),
      continuous: Math.max((d?.continuous ?? 0) + (s?.continuous ?? 0), c?.continuous ?? 0),
      ...(s && {
        requiredAmpacity: Math.max(
          (d?.continuous ?? 0) * headroom + s.continuous,
          (c?.continuous ?? 0) * headroom,
        ),
      }),
    });
  }

  // The walk drains each load to its *hop-nearest* battery, which is the wrong
  // battery once a shunt sits in a bank's negative: the shunt's outlet cable is
  // equidistant from both seeds, so a whole bank's return reads 0 A. Fold the
  // bank's own figure in as a **floor**, never as a replacement — a trunk cable
  // has no battery endpoint, so a reading here may only be raised.
  for (const [cableId, amps] of computeBatteryBankCurrents(
    system,
    defLookup,
    simulate,
  ).trunkByCable) {
    const cur = map.get(cableId);
    map.set(cableId, {
      peak: Math.max(cur?.peak ?? 0, amps),
      continuous: Math.max(cur?.continuous ?? 0, amps),
      ...(cur?.requiredAmpacity != null && {
        requiredAmpacity: Math.max(cur.requiredAmpacity, amps * headroom),
      }),
    });
  }
  const envelopes = !simulate ? currentEnvelope(designGroups, headroom) : new Map();
  // Keep the disconnected branch provisional, but its absent source cannot
  // change a complete circuit's signed envelope on the common return. Other
  // unresolved devices still take precedence through fallbackCableIds.
  for (const id of isolatedLoadFallbackCableIds) if (!envelopes.has(id)) fallbackCableIds.add(id);
  if (pv) {
    // A shared return may carry both PV and battery-side DC contributions.
    // These independently unsigned envelopes cannot certify their joint
    // current. Retain a provisional estimate until signed coupling is modeled.
    const shared = new Set([...pv.byCable.keys()].filter(id => {
      const dc = envelopes.get(id);
      return dc != null && Math.max(dc.continuous, dc.peak) > 0;
    }));
    if (shared.size > 0) {
      markConnectedRailFallback(cableEnds(shared));
      const nodeIds = new Set(system.cables.filter(c => shared.has(c.id))
        .flatMap(c => [c.fromNodeId, c.toNodeId]));
      for (const nodeId of nodeIds) unresolvedComponents.push({ nodeId, reason: "unsupported-pv-array" });
    }
  }
  // Preserve overlapping operating uncertainty before adding design-only
  // sizing rails. Unknown loads or unresolved topology must still suppress
  // their affected live readings, even beside a capped converter.
  const operatingFallbackCableIds = new Set(fallbackCableIds);
  for (const id of designOnlyFallbackCableIds) fallbackCableIds.add(id);
  // Replacement also includes zero: the old unsigned walk may credit a bank
  // whose positive rail is reachable but whose return cannot close the loop.
  // A missing entry in the complete envelope must erase that old share, not
  // leave it available for local sizing to revive as a phantom current.
  for (const id of representedLegacyCableIds) if (!envelopes.has(id) && !fallbackCableIds.has(id)) map.set(id, {
    continuous: 0, peak: 0, requiredAmpacity: 0, currentBasis: "complete-route-envelope",
  });
  // The legacy walk supplies a duty-aware ampacity for starting circuits.
  // Its ordinary load and bank estimates still need the profile's sustained margin.
  const fallbackAmpacity = (demand: CableDemand | undefined) =>
    demand?.requiredAmpacity ?? (demand?.continuous ?? 0) * headroom;
  for (const [id, envelope] of envelopes) {
    const fallback = fallbackCableIds.has(id);
    const previous = fallback ? map.get(id) : undefined;
    map.set(id, {
      continuous: Math.max(envelope.continuous, previous?.continuous ?? 0),
      peak: Math.max(envelope.peak, previous?.peak ?? 0),
      requiredAmpacity: Math.max(envelope.requiredAmpacity, fallbackAmpacity(previous)),
      // Known starting duty still needs peak-current cable sizing even when
      // another transfer makes the total provisional. Its magnitude is a
      // declared contribution, not a complete current-sharing solution.
      startingCurrent: envelope.startingCurrent,
      ...(!fallback && { sustainedCurrent: envelope.sustainedCurrent }),
      currentBasis: fallback ? "provisional-fallback" : "complete-route-envelope",
      ...(sourceAllocationDependentCableIds.has(id) && { sourceAllocationDependent: true }),
    });
  }
  if (pv) {
    for (const [id, amps] of pv.byCable) {
      const previous = map.get(id), fallback = fallbackCableIds.has(id);
      map.set(id, {
        continuous: fallback ? Math.max(amps, previous?.continuous ?? 0) : amps,
        peak: fallback ? Math.max(amps, previous?.peak ?? 0) : amps,
        requiredAmpacity: fallback ? Math.max(amps * headroom, fallbackAmpacity(previous)) : amps * headroom,
        ...(fallback ? {
          ...(previous?.startingCurrent != null && { startingCurrent: previous.startingCurrent }),
          ...(previous?.sourceAllocationDependent && { sourceAllocationDependent: true }),
        } : { sustainedCurrent: amps }),
        currentBasis: fallback ? "provisional-fallback" : "complete-route-envelope",
      });
    }
  }
  // Uncertainty can reach a cable which the legacy estimate omits. Supply an
  // explicit provisional hint so computeCableSpec preserves its local/bank
  // fallback instead of presenting that fallback without provenance.
  for (const id of fallbackCableIds) map.set(id, {
    continuous: 0, peak: 0, ...map.get(id), currentBasis: "provisional-fallback",
  });
  // Live currents have one shared dispatch/battery ledger. Its solved values
  // supersede nameplate bounds; unresolved special circuits retain the design
  // walk (for example starter cranking and unsupported operating points).
  if (simulate) for (const [id, signed] of computeLiveCableCurrents(nodesById, system.cables, defLookup, simulate)) {
    const amps = Math.abs(signed);
    if (amps > 0) map.set(id, { continuous: amps, peak: amps });
    else map.delete(id);
  }
  // Nothing DC is credited to an AC conductor, whatever a PE bond let through.
  for (const c of system.cables) {
    if (isAcCable(c)) map.delete(c.id);
    if (sourceAllocationDependentCableIds.has(c.id)) {
      sourceAllocationDependentNodeIds.add(c.fromNodeId);
      sourceAllocationDependentNodeIds.add(c.toNodeId);
    }
  }
  return { demands: map, unresolvedComponents, fallbackCableIds, operatingFallbackCableIds, sourceAllocationDependentCableIds, sourceAllocationDependentNodeIds };
}
